import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Konfigurasi layout HeroIntro yang bisa di-tweak via DebugPanel.
 * Persist di localStorage, sehingga reload tetap menyimpan tweak.
 */

export type PhaseOverride = "auto" | "assemble" | "logoFull" | "hexVideo" | "tv" | "fullscreen" | "ratings";

export interface HeroLayoutConfig {
  // Dimensi stage
  canvasDesktop: number;
  canvasMobile: number;
  tvWDesktop: number;
  tvHDesktop: number;
  tvWMobile: number;
  tvHMobile: number;

  // Inset screen TV (dalam % terhadap stage TV) — area screen di dalam TIVI-SHADOW.png
  tvInsetLeft: number;
  tvInsetRight: number;
  tvInsetTop: number;
  tvInsetBottom: number;

  // Hex (pointy-top) padding dalam % container — 0 = mengisi penuh
  hexPadX: number; // padding kiri/kanan (default 6.7 → regular hex inscribed)
  hexPadY: number; // padding atas/bawah (default 0)

  // Text — desktop
  retrorikaFontSize: number;
  retrorikaLeft: string;
  retrorikaRight: string;
  retrorikaMarginLeft: number;
  retrorikaMarginRight: number;
  retrorikaMarginTop: number;
  retrorikaMarginBottom: number;
  retrorikaWeight: number;
  retrorikaLetterSpacing: string;

  coffeebarFontSize: number;
  coffeebarMarginLeft: number;
  coffeebarMarginRight: number;
  coffeebarMarginTop: number;
  coffeebarMarginBottom: number;
  coffeebarLetterSpacing: string;

  restoFontSize: number;
  restoMarginLeft: number;
  restoMarginRight: number;
  restoMarginTop: number;
  restoMarginBottom: number;
  restoLetterSpacing: string;

  // Text — mobile
  retrorikaFontSizeMobile: number;
  retrorikaMarginBottomMobile: number;
  coffeebarFontSizeMobile: number;
  restoFontSizeMobile: number;

  // Z-index per layer
  zAssemble: number;
  zLogoFull: number;
  zVideo: number;
  zTvFrame: number;
  zRetrorika: number;
  zCoffeebar: number;
  zResto: number;

  // Phase override (untuk preview cepat di debug)
  phaseOverride: PhaseOverride;

  // Counter visibility
  showCounter: boolean;

  // Logo z-index (assemble/loader phase)
  zLogo1: number;
  zLogo2: number;
  zLogo3: number;
  zLogo4: number;
  zLogo5: number;
  zLogo6: number;
  zLogo7: number;
}

export const DEFAULT_CONFIG: HeroLayoutConfig = {
  canvasDesktop: 460,
  canvasMobile: 320,
  tvWDesktop: 640,
  tvHDesktop: 405,
  tvWMobile: 320,
  tvHMobile: 203,

  tvInsetLeft: 17,
  tvInsetRight: 37,
  tvInsetTop: 16,
  tvInsetBottom: 25,

  hexPadX: 14.9,
  hexPadY: 10.5,

  retrorikaFontSize: 160,
  retrorikaLeft: "50%",
  retrorikaRight: "auto",
  retrorikaMarginLeft: 0,
  retrorikaMarginRight: 0,
  retrorikaMarginTop: 0,
  retrorikaMarginBottom: -40,
  retrorikaWeight: 100,
  retrorikaLetterSpacing: "0.005em",

  coffeebarFontSize: 32,
  coffeebarMarginLeft: 0,
  coffeebarMarginRight: -67,
  coffeebarMarginTop: 35,
  coffeebarMarginBottom: 0,
  coffeebarLetterSpacing: "0.06em",

  restoFontSize: 32,
  restoMarginLeft: -67,
  restoMarginRight: 0,
  restoMarginTop: 35,
  restoMarginBottom: 0,
  restoLetterSpacing: "0.06em",

  retrorikaFontSizeMobile: 60,
  retrorikaMarginBottomMobile: 24,
  coffeebarFontSizeMobile: 40,
  restoFontSizeMobile: 40,

  zAssemble: 4,
  zLogoFull: 5,
  zVideo: 6,
  zTvFrame: 7,
  zRetrorika: 8,
  zCoffeebar: 8,
  zResto: 8,

  phaseOverride: "auto",
  showCounter: false,

  zLogo1: 3,
  zLogo2: 4,
  zLogo3: 7,
  zLogo4: 6,
  zLogo5: 7,
  zLogo6: 1,
  zLogo7: 2,
};

const STORAGE_KEY = "retrorikav2.heroLayout.v1";

interface LayoutConfigCtx {
  config: HeroLayoutConfig;
  setConfig: (patch: Partial<HeroLayoutConfig>) => void;
  reset: () => void;
}

const LayoutConfigContext = createContext<LayoutConfigCtx | null>(null);

export function LayoutConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfigState] = useState<HeroLayoutConfig>(() => {
    if (typeof window === "undefined") return DEFAULT_CONFIG;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return DEFAULT_CONFIG;
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_CONFIG, ...parsed };
    } catch {
      return DEFAULT_CONFIG;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch {
      /* ignore */
    }
  }, [config]);

  const setConfig = useCallback((patch: Partial<HeroLayoutConfig>) => {
    setConfigState((prev) => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => setConfigState(DEFAULT_CONFIG), []);

  const value = useMemo(() => ({ config, setConfig, reset }), [config, setConfig, reset]);

  return (
    <LayoutConfigContext.Provider value={value}>{children}</LayoutConfigContext.Provider>
  );
}

export function useLayoutConfig(): LayoutConfigCtx {
  const ctx = useContext(LayoutConfigContext);
  if (!ctx) throw new Error("useLayoutConfig must be used within LayoutConfigProvider");
  return ctx;
}

/** Generate snippet siap copy untuk dipaste ke kode default. */
export function configToSnippet(cfg: HeroLayoutConfig): string {
  const lines: string[] = [];
  lines.push("// Paste ini ke DEFAULT_CONFIG di src/lib/layoutConfig.tsx");
  lines.push("export const DEFAULT_CONFIG: HeroLayoutConfig = {");
  (Object.keys(cfg) as Array<keyof HeroLayoutConfig>).forEach((k) => {
    const v = cfg[k];
    const val = typeof v === "string" ? `"${v}"` : String(v);
    lines.push(`  ${k}: ${val},`);
  });
  lines.push("};");
  return lines.join("\n");
}
